import { makeAutoObservable, runInAction } from 'mobx';
import type RootStore from '../RootStore';

export interface Position {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    speed: number | null;
    timestamp: number;
}

export class GeolocationStore {
    rootStore: RootStore;
    currentPosition: Position | null = null;
    isTracking = false;
    error: GeolocationPositionError | null = null;
    permission: PermissionState = 'prompt';
    private watchId: number | null = null;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
            // watchId: false,
        });
        this.rootStore = rootStore;

        this.checkPermission();
    }

    async checkPermission() {
        try {
            const result = await navigator.permissions.query({ name: 'geolocation' });
            runInAction(() => {
                this.permission = result.state;
            });

            result.addEventListener('change', () => {
                runInAction(() => {
                    this.permission = result.state;
                });
            });
        } catch (error) {
            console.error('Failed to check geolocation permission:', error);
        }
    }

    async requestPermission() {
        return new Promise<void>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                () => {
                    runInAction(() => {
                        this.permission = 'granted';
                    });
                    resolve();
                },
                (error) => {
                    runInAction(() => {
                        this.permission = 'denied';
                        this.error = error;
                    });
                    reject(error);
                }
            );
        });
    }

    async getCurrentPosition(options?: PositionOptions) {
        return new Promise<Position>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const pos = this.extractPosition(position);
                    runInAction(() => {
                        this.currentPosition = pos;
                        this.error = null;
                    });
                    resolve(pos);
                },
                (error) => {
                    runInAction(() => {
                        this.error = error;
                    });
                    reject(error);
                },
                options
            );
        });
    }

    startTracking(options?: PositionOptions) {
        if (this.isTracking) return;

        this.watchId = navigator.geolocation.watchPosition(
            (position) => {
                runInAction(() => {
                    this.currentPosition = this.extractPosition(position);
                    this.error = null;
                });
            },
            (error) => {
                runInAction(() => {
                    this.error = error;
                });
            },
            options
        );

        runInAction(() => {
            this.isTracking = true;
        });
    }

    stopTracking() {
        if (!this.isTracking || this.watchId === null) return;

        navigator.geolocation.clearWatch(this.watchId);

        runInAction(() => {
            this.isTracking = false;
            this.watchId = null;
        });
    }

    private extractPosition(position: GeolocationPosition): Position {
        return {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: position.timestamp,
        };
    }

    get formattedPosition() {
        if (!this.currentPosition) return 'Unknown';
        return `${this.currentPosition.latitude.toFixed(6)}, ${this.currentPosition.longitude.toFixed(6)}`;
    }

    get googleMapsUrl() {
        if (!this.currentPosition) return null;
        return `https://www.google.com/maps?q=${this.currentPosition.latitude},${this.currentPosition.longitude}`;
    }

    calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
        const R = 6371; // Radius of the earth in km
        const dLat = this.deg2rad(lat2 - lat1);
        const dLon = this.deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.deg2rad(lat1)) *
            Math.cos(this.deg2rad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c; // Distance in km
        return d;
    }

    private deg2rad(deg: number) {
        return deg * (Math.PI / 180);
    }

    dispose() {
        this.stopTracking();
    }
}